import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch, useStore } from 'react-redux'
import { Modal, SelectPicker, Table } from 'rsuite'
import { updateUserPermission, getAllUsersInfo, getUsersByDomain, deleteUser } from '../../redux/actions/user'
import LoaderComponent from '../CommonComponents/LoaderComponent'
import CheckboxComponent from '../CommonComponents/CheckboxComponent'
import { checkIsAdmin, checkIsAdminOrModerator } from '../../services/helper'
import Select, { components } from 'react-select'
import { getTwilioPhoneNumbers } from '../../redux/actions/caller'
import { toast } from '../CommonComponents/ToastComponent/toast'
import ModalBody from 'rsuite/lib/Modal/ModalBody'

const { Column, HeaderCell, Cell, Pagination } = Table

export const SelectorCell = ({ rowData, dataKey, onChange, onDelete, onEdit, roleId, isAdmin, ...props }) => {
	return (
		<Cell {...props} className="action-cell">
			<div className="d-flex gap-2">
				<button type="button" className="h4 pt-2 pb-2 rounded mb-0 bg-gray text-white children width-100" onClick={() => onEdit(rowData)}>Edit</button>
				{isAdmin && <button type="button" className="h4 pt-2 pb-2 rounded mb-0 bg-red text-white children width-100" onClick={() => onDelete(rowData)}>Delete</button>}
			</div>
		</Cell>
	)
}

// const [isLoading, setIsloading] = useState(true)
const UserManageComponent = () => {
	const [sortColumn, setSortColumn] = useState('id')
	const [sortType, setSortType] = useState('asc')
	const [limit, setLimit] = useState(10)
	const [page, setPage] = useState(1)
	const [isEdit, setIsEdit] = useState(false)
	const [isMarkAsCaller, setIsMarkAsCaller] = useState(false)
	const [isCallerAdmin, setIsCallerAdmin] = useState(false)
	const [selectedUser, setSelectedUser] = useState(null)
	const [selectedUserRole, setSelectedUserRole] = useState(null)
	const [phoneNumbers, setPhoneNumbers] = useState([])
	const [assignedNumbers, setAssignedNumbers] = useState([])
	const [phoneNo, setPhoneNo] = useState('')
	const [isLoading, setIsLoading] = useState(false)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [userIdToDelete, setUserIdToDelete] = useState(null)
	const [updateListLoader, setUpdateListLoader] = useState(false)
	const [isBulkUpdate, setIsBulkUpdate] = useState(false)

	const store = useStore().getState()
	const dispatch = useDispatch()
	const { user, domain } = store
	const { domainUsers } = user
	const { currentDomain } = domain
	const isAdminOrModerator = checkIsAdminOrModerator(currentDomain)
	const isAdmin = checkIsAdmin(currentDomain)
	const permissionOptions = [
		{
			label: 'User',
			value: 1,
		},
		{
			label: 'Moderator',
			value: 2,
		},
		{
			label: 'Admin',
			value: 3,
		}
	]

	useEffect(() => {
		setUpdateListLoader(true)
		if (currentDomain?.id) {
			dispatch(getUsersByDomain(currentDomain?.id))
		}
		dispatch(getAllUsersInfo())
		setUpdateListLoader(false)
	}, [showDeleteModal])

	useEffect(() => {
		const phoneNumbers = []
		dispatch(getTwilioPhoneNumbers()).then(result => {
			const parser = new DOMParser();
			const xmlDoc = parser.parseFromString(result?.phoneNumbers, 'application/xml');

			// Extract data
			const numbers = xmlDoc.getElementsByTagName('IncomingPhoneNumber');

			for (let i = 0; i < numbers.length; i++) {
				const data = numbers[i];
				const number = data.getElementsByTagName('PhoneNumber')[0].textContent;
				phoneNumbers.push({ label: number, value: number })
			}
			setPhoneNumbers(phoneNumbers)
			setAssignedNumbers(result?.assignedNumbers)
		}).catch(() => {
			toast.show({
				title: 'Warning',
				description: 'Unable to get Twilio phone numbers!',
			})
			setPhoneNumbers([])
			setAssignedNumbers([])
		})
	}, [])

	const tableData = domainUsers.map(u => {
		return {
			...u,
			firstname: u?.user?.firstname,
			lastname: u?.user?.lastname,
			email: u?.user?.email,
			roleName: permissionOptions?.find(option => option?.value === u?.roleId)?.label
		}
	}).slice(limit * (page - 1), limit * page)

	const handleOnChange = async (id, roleId, isMarkAsCaller, phoneNo, userId, isCallerAdmin, isBulkUpdate = false) => {
		if (isMarkAsCaller && (!phoneNo || phoneNo === '')) {
			toast.show({
				title: 'Warning',
				description: 'Please select phone number to assign!',
			})
			return
		}
		setIsLoading(true)
		await dispatch(updateUserPermission({
			id,
			roleId,
			userId,
			isMarkAsCaller,
			phoneNo,
			isCallerAdmin,
			isBulkUpdate
		})).then((res) => {
			setAssignedNumbers(res?.assignedNumbers)
			handleClose()
			setIsLoading(false)
		}).catch(() => {
			setIsLoading(false)
		})
	}
	const handleChangeLimit = dataKey => {
		setPage(1);
		setLimit(dataKey);
	}

	const handleSortTableData = (sortColumn, sortType) => {
		setSortColumn(sortColumn)
		setSortType(sortType)
	}


	const handleEditClick = (userData) => {
		setSelectedUser(userData)
		setSelectedUserRole(userData?.roleId)
		setIsMarkAsCaller(userData?.user?.isCaller)
		setIsCallerAdmin(userData?.user?.isCallerAdmin)
		setIsBulkUpdate(userData?.user?.isBulkUpdate)
		setPhoneNo(userData?.user?.assignedPhoneNumber)
		setIsEdit(!isEdit)
	}

	const onDeleteClick = async (userData) => {
		setUserIdToDelete(userData?.userId)
		setShowDeleteModal(true)
	}

	const onDeleteHandler = () => {
		try {
			setUpdateListLoader(true)
			dispatch(deleteUser({
				userId: userIdToDelete
			})).then((res) => {
				setUpdateListLoader(false)
				setShowDeleteModal(false)
			})
		} catch (err) {
			console.log("err", err)
			setUpdateListLoader(false)
		}
	}

	const onDeleteModalClose = () => {
		setShowDeleteModal(false)
	}

	const handleClose = () => {
		setIsMarkAsCaller(false)
		setSelectedUserRole(null)
		setIsCallerAdmin(false)
		setSelectedUser(null)
		setIsEdit(!isEdit)
		setPhoneNo('')
		setIsBulkUpdate(false)
	}

	const onNumberClick = (phoneNo) => {
		const isNumberAssigned = assignedNumbers?.find(number => number.assignedPhoneNumber === phoneNo)
		if (!isNumberAssigned && phoneNo) {
			setPhoneNo(phoneNo)
		}
	}

	const Option = (props) => {
		const isNumberAssigned = assignedNumbers?.find(number => number.assignedPhoneNumber === props?.value)
		return (
			<div>
				<components.Option {...props} role='button' className={`d-flex align-items-center ${isNumberAssigned ? 'bg-light cursor-not-allowed' : ''}`}
				>
					<h3 className='text-black mb-0'>{props.label}</h3>
					{isNumberAssigned ?
						<h3 className='fw-bold ms-auto mb-0 text-blue'>{isNumberAssigned?.firstname + (isNumberAssigned?.lastname ? (' ' + isNumberAssigned?.lastname) : '')}</h3>
						:
						<></>
					}
				</components.Option>
			</div>
		);
	};

	return (
		currentDomain?.roleId !== 3 && currentDomain?.roleName !== 'admin' ? (
			<h3 className='padding-left text-black mb-0 fw-bold'>You have not permission to view this page</h3>
		) :
			<div className="assign-vendor-component">
				<div className="quality-component1">
					<div className="record-container quality-child-record">
						<div className="record-header flex flex-wrap justify-space-between align-center">
							<div className="record-title">
								<h3 className='mb-0 current-page-title'>Registered Users</h3>
							</div>
						</div>
						<div className={`record-body position-relative${updateListLoader ? ' loading' : ''}  `}>
							{updateListLoader && <LoaderComponent />}
							<Table
								virtualized
								autoHeight
								affixHeader={true}
								data={tableData}
								sortColumn={sortColumn}
								sortType={sortType}
								onSortColumn={handleSortTableData}
							>
								<Column
									width={300}
									resizable
									sortable
									key="email"
								>
									<HeaderCell>User Email</HeaderCell>
									<Cell dataKey="email"></Cell>
								</Column>
								<Column
									width={200}
									resizable
									sortable
									key="firstname"
								>
									<HeaderCell>First Name</HeaderCell>
									<Cell dataKey="firstname"></Cell>
								</Column>
								<Column
									width={200}
									resizable
									sortable
									key="lastname"
								>
									<HeaderCell>Last Name</HeaderCell>
									<Cell dataKey="lastname"></Cell>
								</Column>
								<Column
									width={300}
									resizable
									sortable
									key="roleName"
								>
									<HeaderCell>Role</HeaderCell>
									<Cell dataKey="roleName"></Cell>
								</Column>
								<Column
									width={200}
									fixed="right"
									key="action"
								>
									<HeaderCell className='text-black fw-bold'>Action</HeaderCell>
									<SelectorCell
										onEdit={handleEditClick}
										onDelete={onDeleteClick}
										isAdmin={isAdmin}
									/>
								</Column>
							</Table>
							<div className='pagination'>
								<Pagination
									prev
									next
									first
									last
									showInfo
									maxButtons={5}
									lengthMenu={[
										{
											label: '5',
											value: 5,
										},
										{
											label: '10',
											value: 10,
										},
									]}
									displayLength={limit}
									total={domainUsers?.length}
									limit={limit}
									activePage={page}
									onChangePage={setPage}
									onChangeLength={handleChangeLimit}
								/>
							</div>
						</div>
						{(isEdit || showDeleteModal) &&
							<Modal
								backdrop={'static'}
								className="change-user-role-modal"
								show={isEdit || showDeleteModal}
								size="xs"
								overflow={false}
								onClick={() => { showDeleteModal ? onDeleteModalClose() : handleClose() }}
								onHide={() => { showDeleteModal ? onDeleteModalClose() : handleClose() }}
							>
								<Modal.Header>
									<Modal.Title className='fw-bold text-black'>
										<h3 className='fw-bold mb-0'>{isEdit ? 'Edit User' : 'Delete User'}</h3>
									</Modal.Title>
								</Modal.Header>
								{
									isEdit ? <Modal.Body className={`mt-4 h-75 position-relative${isLoading ? ' loading' : ''}`}>
										{isLoading && <LoaderComponent />}
										<label className='h3 text-black'>User Role</label>
										<Select
											className="role-option h4 mb-0 filter-component-parent"
											options={permissionOptions}
											value={permissionOptions.find(roleOption => roleOption.value === selectedUserRole)}
											onChange={(role) => setSelectedUserRole(role.value)}
										/>
										{isAdmin && (
											<>
												<h3 className='mt-4 text-black'> Sub Roles </h3>
												<div className='mt-4'>
													<CheckboxComponent
														label='Caller Admin'
														onChange={(checked) => { setIsCallerAdmin(checked) }}
														checked={isCallerAdmin}
													/>
												</div>
												<div className='mt-4'>
													<CheckboxComponent
														label='Caller'
														onChange={(checked) => { setIsMarkAsCaller(checked) }}
														checked={isMarkAsCaller}
													/>
												</div>
												<div className='mt-4'>
													<CheckboxComponent
														label='Bulk Update'
														onChange={(checked) => { setIsBulkUpdate(checked) }}
														checked={isBulkUpdate}
													/>
												</div>
												{isMarkAsCaller && (
													selectedUser?.user?.assignedPhoneNumber ?
														<div className='d-flex align-items-center gap-3 mt-3'>
															<h4 className='text-black fw-bold mb-0'>Assigned Number: </h4>
															<h4 className='mb-0 text-black'>{selectedUser?.user?.assignedPhoneNumber}</h4>
														</div>
														:
														<div className='phone-number-assignments mt-3'>
															<h4 className='text-black fw-bold'>Assign Phone Number</h4>
															<Select
																isClearable={true}
																classNamePrefix='react-select-selector'
																className="h4 mb-0 text-black states-to-not-record-call ms-auto"
																placeholder='Select phone number...'
																options={phoneNumbers}
																styles={{
																	container: (base) => ({
																		...base,
																		zIndex: 3
																	}),
																	option: (base) => ({
																		...base,
																		cursor: 'pointer'
																	})
																}}
																components={{ Option }}
																value={phoneNo ? phoneNumbers.find(number =>
																	number?.value === phoneNo
																) : null}
																onChange={(option => { onNumberClick(option?.value) })}
															/>
														</div>
												)}
											</>
										)}

									</Modal.Body> :
										<ModalBody>
											<h4 className='text-black'>Are you sure you want to delete this user?</h4>
										</ModalBody>
								}
								<Modal.Footer>
									<button type="button" className={`h4 mb-0 pt-2 pb-2 ${showDeleteModal ? "bg-red text-white" : "btn-green"} width-100 rounded`}
										onClick={() => {
											showDeleteModal ? onDeleteHandler() :
												handleOnChange(selectedUser['id'], selectedUserRole, isMarkAsCaller, isMarkAsCaller ? phoneNo : '', selectedUser?.user['id'], isCallerAdmin, isBulkUpdate)
										}}
									>
										{showDeleteModal ? 'Delete' : 'Save'}
									</button>
									<button type="button" className="ms-3 h4 mb-0 pt-2 pb-2 bg-gray text-white width-100 rounded"
										onClick={() => { showDeleteModal ? onDeleteModalClose() : handleClose() }}
									>
										Cancel
									</button>
								</Modal.Footer>
							</Modal>
						}
					</div>
				</div>
			</div>
	)
}

export default UserManageComponent